import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient, useCurrentUser } from '@dogdisk/shared'
import { AppLayout } from '../../components/app-layout'
import { MobileScreen, TopBar } from '../../components/mobile-screen'
import { useCoinBalance } from '../../_lib/wallet-api'

const MIN_COIN = 100

type PreviewResp = {
  to_user_id: string
  to_username: string
  amount_coin: number
  fee_coin: number
}
type InitResp = { transfer_id: string; email_verify_code_id: string }

export function TransferPage() {
  const navigate = useNavigate()
  const { user } = useCurrentUser()
  const { data: wallet } = useCoinBalance(user?.user_id)

  const [identifier, setIdentifier] = useState('')
  const [amount, setAmount] = useState<number>(MIN_COIN)
  const [note, setNote] = useState('')
  const [preview, setPreview] = useState<PreviewResp | null>(null)
  const [transferId, setTransferId] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [showSheet, setShowSheet] = useState(false)

  const onPreview = async () => {
    setBusy(true)
    setError(null)
    try {
      const r = await apiClient.post<PreviewResp>('/gapi/user/transfers/preview', {
        to_identifier: identifier,
        amount_coin: amount,
      })
      setPreview(r)
    } catch (e) {
      setError(e instanceof Error ? e.message : '预览失败')
      setPreview(null)
    } finally {
      setBusy(false)
    }
  }

  const onInit = async () => {
    if (!preview) {
      await onPreview()
      return
    }
    setBusy(true)
    setError(null)
    try {
      const r = await apiClient.post<InitResp>('/gapi/user/transfers/init', {
        to_user_id: preview.to_user_id,
        amount_coin: amount,
        note: note || undefined,
      })
      setTransferId(r.transfer_id)
      setShowSheet(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : '发起失败')
    } finally {
      setBusy(false)
    }
  }

  const onConfirm = async () => {
    if (!transferId) return
    setBusy(true)
    setError(null)
    try {
      await apiClient.post('/gapi/user/transfers/confirm', { transfer_id: transferId, code })
      setDone(true)
      setShowSheet(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : '确认失败')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AppLayout>
      <MobileScreen bg="surface">
        <TopBar
          title="转账"
          right={
            <button type="button" onClick={() => navigate('/wallet/transfer/records')} className="text-[13px] text-[#19caad]">
              转账记录
            </button>
          }
        />

        {done ? (
          <main data-testid="ug7Cy" className="flex flex-1 flex-col items-center gap-4 px-6 pt-12">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#ebfaf6] text-[#19caad]">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M5 12l5 5 9-12" /></svg>
            </span>
            <p className="text-[18px] font-semibold text-[#1A1A1A]">转账已完成</p>
            <button onClick={() => navigate('/wallet')} className="mt-4 h-11 w-full max-w-xs rounded-lg bg-[#19caad] text-[14px] font-semibold text-white">
              返回钱包
            </button>
          </main>
        ) : (
          <main data-testid="ug7Cy" className="flex flex-1 flex-col gap-4 px-6 pt-4 pb-32">
            <section className="rounded-2xl bg-gradient-to-br from-[#19caad] via-[#22b9ad] to-[#1d9be0] p-4 text-white">
              <p className="text-[12px] opacity-80">COIN 余额</p>
              <p className="mt-1 flex items-baseline gap-1 text-[32px] font-bold tabular-nums">
                {wallet?.balance ? Number(wallet.balance).toLocaleString() : '0'}
                <span className="text-[12px] font-normal opacity-90">金币</span>
              </p>
              <p className="mt-1 text-[11px] opacity-80">
                ≈ ¥{wallet?.balance ? (Number(wallet.balance) / 100).toFixed(2) : '0.00'}（100 金币 = 1 元）
              </p>
            </section>

            <section>
              <p className="mb-2 text-[14px] font-semibold text-[#1A1A1A]">收方 user_id</p>
              <input
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                onBlur={() => identifier && void onPreview()}
                placeholder="输入对方完整 user_id (UUID)"
                className="h-12 w-full rounded-lg bg-white px-4 text-[14px] outline-none placeholder:text-[#B4B7BC]"
              />
              {preview && (
                <div className="mt-2 flex items-center gap-2 text-[12px] text-[#1A1A1A]">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#a8e9d8] text-[10px] font-semibold text-[#0f7969]">
                    {preview.to_username.slice(0, 1).toUpperCase()}
                  </span>
                  <span className="font-medium">{preview.to_username.slice(0, 5)}***</span>
                  <span className="text-[#19caad]">✓</span>
                </div>
              )}
            </section>

            <section>
              <p className="mb-2 text-[14px] font-semibold text-[#1A1A1A]">转账金额（金币）</p>
              <div className="flex items-center gap-2 rounded-lg bg-white px-4 py-3">
                <span className="text-[12px] text-[#9CA3AF]">≥ {MIN_COIN}</span>
                <input
                  type="number"
                  min={MIN_COIN}
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className="flex-1 bg-transparent text-right text-[16px] font-semibold tabular-nums outline-none"
                />
                <span className="text-[12px] text-[#9CA3AF]">金币</span>
              </div>
              <p className="mt-1 text-[11px] text-[#9CA3AF]">≈ ¥{(amount / 100).toFixed(2)}</p>
            </section>

            <section>
              <p className="mb-2 text-[14px] font-semibold text-[#1A1A1A]">备注（选填）</p>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="请输入转账备注，≤ 200 字符"
                maxLength={200}
                rows={3}
                className="w-full resize-none rounded-lg bg-white px-4 py-3 text-[14px] outline-none placeholder:text-[#B4B7BC]"
              />
            </section>

            {error && <p className="text-[13px] text-[#FF6B6B]">{error}</p>}
          </main>
        )}

        {!done && (
          <div
            className="fixed inset-x-0 bottom-0 z-20 mx-auto max-w-[402px] bg-[#F6F7F8] px-6 pt-3"
            style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
          >
            <button
              type="button"
              onClick={onInit}
              disabled={busy || !identifier || amount < MIN_COIN}
              className="h-12 w-full rounded-lg bg-[#19caad] text-[15px] font-semibold text-white active:bg-[#14a18b] disabled:opacity-50"
            >
              {busy ? '处理中…' : '下一步'}
            </button>
            <p className="mt-2 text-center text-[10px] text-[#9CA3AF]">起步 {MIN_COIN} 金币 / 不收手续费 / 需邮箱验证确认</p>
          </div>
        )}

        {showSheet && preview && (
          <div
            data-frame-id="xbUnW"
            data-testid="xbUnW"
            role="dialog"
            aria-modal="true"
            aria-label="转账确认"
            className="fixed inset-0 z-40 flex items-end bg-black/50"
            onClick={() => setShowSheet(false)}
          >
            <div
              className="mx-auto w-full max-w-[402px] rounded-t-3xl bg-white"
              style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-center pt-2 pb-3">
                <span className="h-1 w-10 rounded-full bg-[#E0E0E0]" />
              </div>
              <div className="flex items-center justify-between px-6 pb-1">
                <span className="text-[14px] font-semibold text-[#1A1A1A]">转账确认</span>
                <button onClick={() => setShowSheet(false)} aria-label="关闭" className="text-[#9CA3AF]">×</button>
              </div>
              <div className="flex flex-col items-center gap-1 px-6 py-4">
                <p className="text-[12px] text-[#9CA3AF]">转账金额</p>
                <p className="flex items-baseline gap-1 text-[32px] font-bold tabular-nums">
                  {amount.toLocaleString()}
                  <span className="text-[14px] font-normal text-[#9CA3AF]">金币</span>
                </p>
              </div>
              <div className="mx-4 mb-3 flex items-center gap-3 rounded-2xl bg-[#F6F7F8] px-4 py-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#a8e9d8] text-[12px] font-semibold text-[#0f7969]">
                  {preview.to_username.slice(0, 1).toUpperCase()}
                </span>
                <div className="flex-1 text-[13px] text-[#1A1A1A]">收方：{preview.to_username}</div>
              </div>
              {note && (
                <div className="px-6 pb-2 text-[12px] text-[#9CA3AF]">备注：{note}</div>
              )}
              <div className="flex flex-col gap-2 px-4 pb-2">
                <p className="text-[12px] text-[#9CA3AF]">邮箱验证码</p>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="6 位验证码"
                  className="h-11 rounded-lg bg-[#F6F7F8] px-4 text-[14px] outline-none placeholder:text-[#B4B7BC]"
                />
                {error && <p className="text-[12px] text-[#FF6B6B]">{error}</p>}
              </div>
              <div className="flex gap-3 px-4 pb-3 pt-2">
                <button
                  onClick={() => setShowSheet(false)}
                  className="h-11 flex-1 rounded-lg bg-[#F1F2F4] text-[14px] font-semibold text-[#1A1A1A]"
                >
                  取消
                </button>
                <button
                  onClick={onConfirm}
                  disabled={busy || code.length !== 6}
                  className="h-11 flex-1 rounded-lg bg-[#19caad] text-[14px] font-semibold text-white disabled:opacity-50"
                >
                  {busy ? '确认中…' : '确认转账'}
                </button>
              </div>
            </div>
          </div>
        )}
      </MobileScreen>
    </AppLayout>
  )
}
